import './walcom-product.js';
import './walcom-commerce.js';

const initialized = new WeakSet();
const pending = new WeakMap();

async function hydrateRemote(host, url) {
  if (pending.has(host)) return pending.get(host);
  const task = (async () => {
    const response = await fetch(url, {credentials:'same-origin'});
    if (!response.ok) throw new Error('Section request failed');
    const html = await response.text();
    if (!host.isConnected) return;
    const doc = new DOMParser().parseFromString(html,'text/html');
    const currentId = host.id;
    const replacement = currentId ? doc.getElementById(currentId) : null;
    if (replacement) {
      host.replaceChildren(...replacement.childNodes);
    } else {
      const content = doc.querySelector('[data-products-container], .products-grid, .products-carousel');
      if (content) host.replaceChildren(...content.childNodes);
    }
    host.dataset.walcomLoaded='true';
    init(host);
    document.dispatchEvent(new CustomEvent('walcom:section-loaded',{detail:{root:host}}));
  })();
  pending.set(host,task);
  try { await task; } finally { pending.delete(host); }
}

function carousel(track) {
  if (initialized.has(track)) return;
  const items=Array.from(track.children).filter(el=>el.classList.contains('product'));
  if (!items.length) return;
  initialized.add(track);
  track.classList.add('walcom-products-track');
  const count=Number(track.dataset.itemToShow)||4;
  track.style.setProperty('--walcom-products-columns',String(count));
  if (track.dataset.itemArrows==='true') {
    const host=track.parentElement;
    const controls=document.createElement('div');
    controls.className='walcom-products-controls';
    for (const [label,direction] of [['Previous',-1],['Next',1]]) {
      const button=document.createElement('button');
      button.type='button';
      button.className=direction<0?'slick-prev':'slick-next';
      button.textContent=direction<0?'‹':'›';
      button.setAttribute('aria-label',label);
      button.addEventListener('click',()=>track.scrollBy({left:direction*track.clientWidth,behavior:'smooth'}));
      controls.append(button);
    }
    host.append(controls);
  }
}

function restoreBreadcrumbs(root) {
  root.querySelectorAll('.walcom-legacy.template-product breadcrumb-component').forEach(host => {
    if (host.dataset.walcomBreadcrumb) return;
    host.dataset.walcomBreadcrumb = 'true';
    const nav=host.querySelector('nav');
    if (!nav || nav.querySelectorAll('a').length > 1) return;
    let collection;
    try { collection=JSON.parse(localStorage.getItem('_breadcrumb_collection') || 'null'); } catch {}
    const useCollection=collection?.show && collection.url && document.referrer.includes('/collections/') && new URL(document.referrer).pathname===collection.url;
    const link=document.createElement('a');
    link.className='breadcrumb-collection animate--text';
    link.href=useCollection?collection.url:host.dataset.collectionAll;
    link.textContent=useCollection?collection.name:'Products';
    const separator=nav.querySelector('.separate');
    if (separator) separator.after(link,separator.cloneNode(true));
  });
}

function initProductAccordions(root) {
  root.querySelectorAll('.walcom-legacy product-tab.halo-product-tab .tabs-contents-horizontal > .tab-content').forEach(panel => {
    if (panel.querySelector('.walcom-product-accordion')) return;
    const title = panel.querySelector('.toggle-title');
    const body = panel.querySelector('.toggle-content');
    if (!title || !body) return;
    const defaultMobileOpen = body.classList.contains('show-mobile');
    const accordion = document.createElement('accordion-custom');
    accordion.className = 'walcom-product-accordion';
    accordion.setAttribute('open-by-default-on-desktop', '');
    if (defaultMobileOpen) accordion.setAttribute('open-by-default-on-mobile', '');
    const details = document.createElement('details');
    details.className = 'details';
    details.open = window.innerWidth > 550 || defaultMobileOpen;
    const summary = document.createElement('summary');
    summary.className = 'details__header';
    summary.append(...title.querySelector('.toggleLink').childNodes);
    body.className = 'details-content walcom-product-tab-content';
    body.hidden = false;
    body.querySelector('.tab-popup-header')?.remove();
    const popupContent = body.querySelector('.tab-popup-content');
    if (popupContent) popupContent.replaceWith(...popupContent.childNodes);
    details.append(summary, body);
    accordion.append(details);
    title.replaceWith(accordion);
    panel.hidden = false;
  });
}

function activateProductTab(link, { updateHash = true } = {}) {
  const host = link.closest('.productView-tab.halo-product-tab');
  if (!host) return false;
  const href = link.getAttribute('href');
  if (!href?.startsWith('#')) return false;
  const panel = host.querySelector(href);
  if (!panel?.classList.contains('tab-content')) return false;

  host.querySelectorAll('.tabs-horizontal .tab-title').forEach(tab => {
    const active = tab === link;
    tab.classList.toggle('is-open', active);
    tab.closest('.tab')?.classList.toggle('is-active', active);
    tab.setAttribute('aria-selected', String(active));
  });
  host.querySelectorAll('.tabs-contents-horizontal > .tab-content').forEach(content => {
    const active = content === panel;
    content.classList.toggle('is-active', active);
    content.hidden = false;
    const details = content.querySelector('.walcom-product-accordion details');
    if (details) details.open = active || window.innerWidth > 550;
  });
  if (updateHash) history.replaceState(null, '', `${location.pathname}${location.search}${href}`);
  return true;
}

function init(root=document) {
  restoreBreadcrumbs(root);
  initProductAccordions(root);
  root.querySelectorAll('.walcom-legacy img.lazyload,.walcom-legacy img[data-src],.walcom-legacy img[data-srcset]').forEach(image=>{
    if(image.dataset.src) image.src=image.dataset.src;
    if(image.dataset.srcset) image.srcset=image.dataset.srcset;
    image.classList.remove('lazyload','lazyloading');
    image.classList.add('lazyloaded');
  });
  root.querySelectorAll('.walcom-legacy .products-carousel').forEach(carousel);
  root.querySelectorAll('.walcom-legacy [data-product-tab-block]').forEach(host=>{
    if(initialized.has(host))return;
    initialized.add(host);
    const buttons=Array.from(host.querySelectorAll('[data-product-tabtop]'));
    for (const button of buttons) {
      const panel=host.querySelector(button.dataset.target);
      if(!panel)continue;
      button.setAttribute('role','tab');
      button.setAttribute('aria-controls',panel.id);
      const active=button.classList.contains('active');
      button.setAttribute('aria-selected',String(active));
      panel.setAttribute('role','tabpanel');
      panel.hidden=!active;
    }
    host.addEventListener('click',event=>{
      const button=event.target.closest('[data-product-tabtop]');
      if(!button)return;
      const target=host.querySelector(button.dataset.target);
      if(!target)return;
      for(const other of buttons){
        const active=other.dataset.target===button.dataset.target;
        other.classList.toggle('active',active);
        other.setAttribute('aria-selected',String(active));
        const panel=host.querySelector(other.dataset.target);
        if(panel){panel.hidden=!active;panel.classList.toggle('active',active);panel.classList.toggle('show',active);}
      }
      target.querySelectorAll('.products-carousel').forEach(carousel);
    });
  });
  root.querySelectorAll('.walcom-legacy product-recommendations[data-url]').forEach(host=>{
    if(initialized.has(host))return;
    initialized.add(host);
    const observer=new IntersectionObserver(entries=>{
      if(!entries.some(entry=>entry.isIntersecting))return;
      observer.disconnect();
      hydrateRemote(host,host.dataset.url).catch(()=>{host.dataset.walcomLoadError='true';});
    },{rootMargin:'300px'});
    observer.observe(host);
  });
  root.querySelectorAll('.walcom-legacy [data-tab]').forEach(button=>{
    if(initialized.has(button))return;
    const href=button.getAttribute('href');
    if(!href?.startsWith('#'))return;
    const panel=document.getElementById(href.slice(1));
    if(!panel)return;
    initialized.add(button);
    button.addEventListener('click',event=>{
      event.preventDefault();
      const tabs=button.closest('.productView-tab,.product-tabs,.tabs');
      tabs?.querySelectorAll('[data-tab]').forEach(other=>other.classList.toggle('active',other===button));
      panel.parentElement?.querySelectorAll(':scope > .tab-content,:scope > .tab-pane').forEach(other=>other.classList.toggle('active',other===panel));
      panel.classList.add('active');
    });
  });
  root.querySelectorAll('.walcom-legacy product-tab.halo-product-tab .tabs-horizontal .tab-title[href^="#tab-"]').forEach(link => {
    link.setAttribute('role', 'tab');
    link.setAttribute('aria-selected', String(link.classList.contains('is-open')));
  });
  root.querySelectorAll('.walcom-legacy .footer-block__mobile .footer-block__heading[data-toggle-column-footer]').forEach(heading=>{
    if(initialized.has(heading))return;
    initialized.add(heading);
    heading.setAttribute('role', 'button');
    heading.tabIndex = 0;
    heading.setAttribute('aria-expanded', String(heading.classList.contains('is-clicked')));
    const toggle = () => {
      const open = heading.classList.toggle('is-clicked');
      heading.setAttribute('aria-expanded', String(open));
    };
    heading.addEventListener('click', toggle);
    heading.addEventListener('keydown', event => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      toggle();
    });
  });
}

document.addEventListener('change',event=>{
  const input=event.target;
  if(!(input instanceof HTMLElement)||!input.closest('.walcom-legacy'))return;
  if(input.matches('[name="sort_by"],[name^="filter."]')){
    const form=input.closest('form');
    if(form)form.requestSubmit();
  }
});
document.addEventListener('click',event=>{
  const productTab = event.target.closest('.walcom-legacy product-tab.halo-product-tab .tabs-horizontal .tab-title[href^="#tab-"]');
  if (productTab && activateProductTab(productTab)) {
    event.preventDefault();
    return;
  }
  const trigger=event.target.closest('.walcom-legacy [data-fancybox]');
  if(!trigger)return;
  const href=trigger.getAttribute('href');
  if(!href||!/.(jpe?g|png|webp)(?:\?|$)/i.test(href))return;
  event.preventDefault();
  const dialog=document.createElement('dialog');
  dialog.className='walcom-image-dialog';
  const image=document.createElement('img');
  image.src=href;image.alt=trigger.querySelector('img')?.alt||'';
  const close=document.createElement('button');
  close.type='button';close.textContent='×';close.setAttribute('aria-label','Close');
  close.addEventListener('click',()=>dialog.close());
  dialog.append(close,image);
  dialog.addEventListener('close',()=>dialog.remove(),{once:true});
  document.body.append(dialog);dialog.showModal();
});
document.addEventListener('shopify:section:load',event=>init(event.target));
document.addEventListener('walcom:product-updated',event=>init(event.target));
if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>init(),{once:true});else init();
function activateLinkedProductTab() {
  if (!location.hash.startsWith('#tab-')) return;
  const targetId = decodeURIComponent(location.hash.slice(1));
  const target = document.getElementById(targetId);
  const host = target?.closest('.walcom-legacy product-tab.halo-product-tab');
  if (!host) return;

  const panel = target.classList.contains('tab-content') ? target : target.closest('.tab-content');
  if (!panel) return;
  host.querySelectorAll('.tabs-contents-horizontal > .tab-content').forEach(other => {
    const active = other === panel;
    other.classList.toggle('is-active', active);
    other.hidden = false;
    const details = other.querySelector('.walcom-product-accordion details');
    if (details) details.open = active || window.innerWidth > 550;
  });
  const topLink = Array.from(host.querySelectorAll('.tabs-horizontal .tab-title')).find(link => link.getAttribute('href') === `#${panel.id}`);
  if (topLink) {
    topLink.classList.add('is-open');
    topLink.setAttribute('aria-selected', 'true');
  }
  if (target.hidden) target.hidden = false;
  requestAnimationFrame(() => target.scrollIntoView({ block: 'start', behavior: 'smooth' }));
}
window.addEventListener('hashchange', activateLinkedProductTab);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', activateLinkedProductTab, { once: true });
else activateLinkedProductTab();

window.addEventListener('resize', () => {
  if (window.innerWidth > 550) {
    document.querySelectorAll('.walcom-product-accordion details').forEach(details => { details.open = true; });
  }
});
